import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { hotSearch, siteStats } from "@/mocks/categories";

export default function Hero() {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/topics?q=${encodeURIComponent(q)}` : "/topics");
  };

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20poster%20art%20with%20flowing%20warm%20coral%20amber%20and%20teal%20ribbons%20over%20a%20deep%20charcoal%20cream%20blend%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20magazine%20cover%20aesthetic%2C%20no%20text%2C%20no%20people%2C%20generous%20negative%20space&width=1600&height=1000&seq=gt-hero-main&orientation=landscape"
          alt="瓜田观察站主视觉：抽象暖色几何构成"
          title="瓜田观察站 · 网络热点事件档案"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/55 to-foreground-950/80"></div>
      </div>

      <div className="relative mx-auto flex w-full max-w-7xl flex-col px-4 pb-14 pt-28 md:px-6 md:pb-20 md:pt-40">
        <div className="mx-auto w-full max-w-3xl text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3.5 py-1.5 font-label text-xs tracking-wide text-background-100 backdrop-blur">
            <span className="flex h-1.5 w-1.5 animate-pulse-soft rounded-full bg-accent-400"></span>
            公开信息整理 · 时间线归档 · 理性围观
          </p>
          <h1 className="mt-6 font-display text-4xl leading-tight text-background-50 md:text-6xl">
            瓜田观察站
          </h1>
          <p className="mx-auto mt-5 max-w-2xl font-body text-base leading-relaxed text-background-200 md:text-lg">
            网络热点每天都在刷新，但真正难的是看清脉络。我们把散落各处的公开信息收进档案，
            按分类归档、用时间线排列，让你在围观时始终知道事情走到哪一步。
          </p>

          <form
            onSubmit={handleSubmit}
            className="mx-auto mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row"
            role="search"
          >
            <label className="relative flex-1">
              <span className="sr-only">搜索事件档案</span>
              <i
                className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                aria-hidden="true"
              ></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索事件关键词，如「排期调整」「优惠机制」"
                className="h-12 w-full rounded-md border border-background-200 bg-background-50 pl-11 pr-4 font-body text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
              />
            </label>
            <button type="submit" className="btn-primary h-12 shrink-0 px-6">
              搜索档案
            </button>
          </form>

          <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {siteStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-background-50/15 bg-background-50/10 px-4 py-4 text-center backdrop-blur"
              >
                <i className={`${stat.icon} text-lg text-accent-300`} aria-hidden="true"></i>
                <p className="mt-2 font-heading text-xl font-bold text-background-50 md:text-2xl">
                  {stat.value}
                </p>
                <p className="mt-1 font-label text-xs text-background-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative border-t border-background-50/15 bg-foreground-950/70 py-3 backdrop-blur">
        <div className="flex items-center gap-4 px-4 md:px-6">
          <span className="flex shrink-0 items-center gap-1.5 font-label text-xs font-medium text-accent-300">
            <i className="ri-flashlight-line" aria-hidden="true"></i>
            实时播报
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee items-center gap-8">
              {[...hotSearch, ...hotSearch].map((item, index) => (
                <span
                  key={`${item.title}-${index}`}
                  className="whitespace-nowrap font-body text-xs text-background-200 md:text-sm"
                >
                  <span className="mr-2 font-label text-accent-400">#{item.rank}</span>
                  {item.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}